import global from "../../../common/utility/global";
import {
    COLORES_ESTADO,
    COLORES_ESTADO_OTROS,
    TRAMOS_CUENTA_COBRAR,
    NOMBRES_MES_CORTO,
    RESUMEN_FINANCIERO_EN_DURO,
} from "./constants";

export function formatCurrency(value, moneda = "PEN") {
    const numero = Number(value) || 0;
    const simbolo = moneda === "USD" ? "US$" : "S/";
    return `${simbolo} ${global.decimalFormat(numero.toFixed(2))}`;
}

export function formatNumber(value, decimales = 1) {
    const numero = Number(value) || 0;
    return global.decimalFormat(numero.toFixed(decimales));
}

const MAX_CARACTERES_NOMBRE = 18;

// Recorta nombres largos para que la leyenda/tooltip mantengan siempre el
// mismo ancho; sin esto, un nombre muy largo estira la leyenda y deforma el
// layout del grafico.
export function truncarNombre(nombre) {
    if (!nombre) return "";
    return nombre.length > MAX_CARACTERES_NOMBRE
        ? `${nombre.slice(0, MAX_CARACTERES_NOMBRE)}...`
        : nombre;
}

// "EMITIDO" -> "Emitido"
function capitalizar(texto) {
    if (!texto) return "";
    const minusculas = String(texto).toLowerCase();
    return minusculas.charAt(0).toUpperCase() + minusculas.slice(1);
}

// " 1 - 30 días " -> "1 - 30 DIAS", para comparar textos del backend sin
// depender de mayusculas ni tildes.
function normalizar(texto) {
    return String(texto || "")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .trim()
        .toUpperCase();
}

// "2026-08-27" -> Date local (evita el corrimiento de dia que da
// "new Date('2026-08-27')" al interpretarse como UTC en un huso horario negativo).
function parsearFechaLocal(fechaStr) {
    const [anio, mes, dia] = fechaStr.slice(0, 10).split("-").map(Number);
    return new Date(anio, mes - 1, dia);
}

function obtenerClaveTiempo(fecha, esMismoMes) {
    return esMismoMes
        ? `${fecha.getDate()} ${NOMBRES_MES_CORTO[fecha.getMonth()]}`
        : `${NOMBRES_MES_CORTO[fecha.getMonth()]} ${fecha.getFullYear()}`;
}

// Linea de tiempo armada solo con el filtro (dias del mes, o meses del
// rango). Se usa cuando el servicio no devuelve historial, para que el
// grafico igual pinte sus ejes con la serie en 0 en vez de quedar en blanco.
function construirLineaTiempoVacia(filter, esMismoMes) {
    const anioInicio = Number(filter.anioInicio);
    const mesInicio = Number(filter.mesInicio);
    const anioFin = Number(filter.anioFin);
    const mesFin = Number(filter.mesFin);

    if (esMismoMes) {
        const diasDelMes = new Date(anioInicio, mesInicio, 0).getDate();
        return Array.from({ length: diasDelMes }, (_, indice) => `${indice + 1} ${NOMBRES_MES_CORTO[mesInicio - 1]}`);
    }

    const claves = [];
    let anio = anioInicio;
    let mes = mesInicio;
    while ((anio < anioFin || (anio === anioFin && mes <= mesFin)) && claves.length < 60) {
        claves.push(`${NOMBRES_MES_CORTO[mes - 1]} ${anio}`);
        mes += 1;
        if (mes > 12) {
            mes = 1;
            anio += 1;
        }
    }
    return claves.length > 0 ? claves : [`${NOMBRES_MES_CORTO[mesInicio - 1]} ${anioInicio}`];
}

// Arma "Ventas vs Compras" a partir del historial diario del servicio
// (ventaSoles/ventaDolares y compraSoles/compraDolares). Se agrupa por dia
// si el filtro cae en un mismo mes, o por mes si abarca mas de uno.
function construirVentasCompras(historial, filter, esMismoMes) {
    const filas = historial
        .filter((fila) => fila && fila.fecha)
        .map((fila) => ({ ...fila, __fecha: parsearFechaLocal(fila.fecha) }))
        .sort((a, b) => a.__fecha - b.__fecha);

    const ordenClaves = [];
    const acumulado = {};
    filas.forEach((fila) => {
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        if (!acumulado[clave]) {
            acumulado[clave] = { ventasPen: 0, ventasUsd: 0, comprasPen: 0, comprasUsd: 0 };
            ordenClaves.push(clave);
        }
        acumulado[clave].ventasPen += Number(fila.ventaSoles) || 0;
        acumulado[clave].ventasUsd += Number(fila.ventaDolares) || 0;
        acumulado[clave].comprasPen += Number(fila.compraSoles) || 0;
        acumulado[clave].comprasUsd += Number(fila.compraDolares) || 0;
    });

    const timeLine = ordenClaves.length > 0 ? ordenClaves : construirLineaTiempoVacia(filter, esMismoMes);
    const construirSerie = (campo) => timeLine.map((clave) => Number(((acumulado[clave] || {})[campo] || 0).toFixed(2)));

    return {
        timeLine,
        pen: { ventas: construirSerie("ventasPen"), compras: construirSerie("comprasPen") },
        usd: { ventas: construirSerie("ventasUsd"), compras: construirSerie("comprasUsd") },
    };
}

function direccionTendencia(actual, anterior) {
    if (actual > anterior) return "up";
    if (actual < anterior) return "down";
    return "flat";
}

// % de variacion actual vs anterior. Si el periodo anterior no tuvo
// movimiento, se evita la division entre 0: sin cambio real (0/0) queda
// plano, y una aparicion nueva (valor/0) se marca como +100%.
function calcularTendenciaPct(actual, anterior) {
    if (!anterior) return actual > 0 ? 100 : 0;
    return ((actual - anterior) / Math.abs(anterior)) * 100;
}

// Si el servicio no manda el dato (o no manda su par "Anterior"), el KPI
// queda en 0 en vez de romper la tarjeta.
function construirKpi(actual, anterior) {
    const valorActual = Number(actual) || 0;
    const valorAnterior = Number(anterior) || 0;
    return {
        valor: valorActual,
        tendenciaPct: calcularTendenciaPct(valorActual, valorAnterior),
        tendenciaDireccion: direccionTendencia(valorActual, valorAnterior),
    };
}

function construirKpiMoneda(actual, anterior, campoSoles, campoDolares) {
    return {
        pen: construirKpi(actual[campoSoles], anterior[campoSoles]),
        usd: construirKpi(actual[campoDolares], anterior[campoDolares]),
    };
}

function dividir(numerador, denominador) {
    const divisor = Number(denominador) || 0;
    return divisor > 0 ? (Number(numerador) || 0) / divisor : 0;
}

// Agrupa las filas por nombre y devuelve cada grupo con su total y su % de
// participacion, de mayor a menor. Se omiten los grupos sin monto.
function construirDistribucion(filas, campoNombre, campoTotal) {
    const acumulado = {};
    filas.forEach((fila) => {
        const nombre = fila[campoNombre] || "Sin nombre";
        acumulado[nombre] = (acumulado[nombre] || 0) + (Number(fila[campoTotal]) || 0);
    });

    const totalGeneral = Object.keys(acumulado).reduce((suma, nombre) => suma + acumulado[nombre], 0);
    return Object.keys(acumulado)
        .map((nombre) => ({
            nombre,
            total: acumulado[nombre],
            porcentaje: totalGeneral > 0 ? (acumulado[nombre] / totalGeneral) * 100 : 0,
        }))
        .filter((item) => item.total > 0)
        .sort((a, b) => b.total - a.total);
}

// Cuentas por cobrar por antiguedad. Los tramos conocidos salen siempre en
// el mismo orden y color (Al corriente -> > 60 dias); un tramo que no se
// reconozca va al final con un color de reserva.
function construirCuentasCobrar(estadoCC, campoTotal) {
    let indiceOtros = 0;
    return construirDistribucion(estadoCC, "nombre", campoTotal)
        .map((item) => {
            const nombreNormalizado = normalizar(item.nombre);
            const orden = TRAMOS_CUENTA_COBRAR.findIndex((tramo) => tramo.claves.includes(nombreNormalizado));
            if (orden >= 0) {
                const tramo = TRAMOS_CUENTA_COBRAR[orden];
                return { ...item, nombre: tramo.nombre, color: tramo.color, orden };
            }
            const color = COLORES_ESTADO_OTROS[indiceOtros % COLORES_ESTADO_OTROS.length];
            indiceOtros += 1;
            return { ...item, color, orden: TRAMOS_CUENTA_COBRAR.length };
        })
        .sort((a, b) => a.orden - b.orden);
}

// Cantidad de documentos por estado (Ordenes de Compra / Cotizaciones).
function construirEstados(estadosApi) {
    let indiceOtros = 0;
    return construirDistribucion(estadosApi, "nombre", "total").map((item) => {
        let color = COLORES_ESTADO[normalizar(item.nombre)];
        if (!color) {
            color = COLORES_ESTADO_OTROS[indiceOtros % COLORES_ESTADO_OTROS.length];
            indiceOtros += 1;
        }
        return { ...item, nombre: capitalizar(item.nombre), color };
    });
}

// El servicio manda un arreglo de un solo elemento con los totales del
// inventario; si no viene, todo queda en 0.
function construirInventario(inventarioApi) {
    return {
        productosTotales: Number(inventarioApi.productosTotales) || 0,
        productosStockBajo: Number(inventarioApi.productosStockBajo) || 0,
        productosSinStock: Number(inventarioApi.productosSinStock) || 0,
        valorInventario: {
            pen: Number(inventarioApi.valorTotalSoles) || 0,
            usd: Number(inventarioApi.valorTotalDolares) || 0,
        },
    };
}

function construirResumenFinanciero(clave) {
    const ingresos = RESUMEN_FINANCIERO_EN_DURO.ingresos[clave];
    const gastos = RESUMEN_FINANCIERO_EN_DURO.gastos[clave];
    const utilidadNeta = ingresos - gastos;
    return {
        ingresos,
        gastos,
        utilidadNeta,
        margenUtilidad: dividir(utilidadNeta, ingresos) * 100,
    };
}

export function buildDashboardData(response, filter) {
    // El servicio (comprobanteService.dashboardGeneral) devuelve:
    // - kpis: dos filas, periodo "ACTUAL" y "ANTERIOR", con ventas, compras
    //   y utilidad en soles y dolares.
    // - historial: ventas y compras por dia del periodo.
    // - estadoCC: cuentas por cobrar por tramo de antiguedad (montos).
    // - estadoOC / estadoCT: cantidad de ordenes de compra / cotizaciones
    //   por estado.
    // - inventario: un solo elemento con productos totales, con stock bajo,
    //   sin stock y valor total en soles y dolares.
    // - resumenFin: aun en desarrollo; se usan datos en duro (ver constants.js).
    // Cualquier bloque ausente se trata como vacio para que el dashboard
    // muestre ceros en lugar de quedar en blanco.
    const kpisApi = (response && response.kpis) || [];
    const buscarPeriodo = (periodo) => kpisApi.find((fila) => normalizar(fila && fila.periodo) === periodo) || {};
    const kpiActual = buscarPeriodo("ACTUAL");
    const kpiAnterior = buscarPeriodo("ANTERIOR");

    const historial = (response && response.historial) || [];
    const estadoCC = (response && response.estadoCC) || [];
    const estadoOC = (response && response.estadoOC) || [];
    const estadoCT = (response && response.estadoCT) || [];
    const inventarioApi = (response && response.inventario && response.inventario[0]) || {};

    const esMismoMes = filter.anioInicio === filter.anioFin && filter.mesInicio === filter.mesFin;

    return {
        esMismoMes,
        kpis: {
            ventas: construirKpiMoneda(kpiActual, kpiAnterior, "ventasSoles", "ventasDolares"),
            compras: construirKpiMoneda(kpiActual, kpiAnterior, "comprasSoles", "comprasDolares"),
            utilidad: construirKpiMoneda(kpiActual, kpiAnterior, "utilidadSoles", "utilidadDolares"),
            // El servicio aun no manda flujo de caja: la tarjeta queda en 0
            // hasta que lleguen flujoCajaSoles / flujoCajaDolares.
            flujoCaja: construirKpiMoneda(kpiActual, kpiAnterior, "flujoCajaSoles", "flujoCajaDolares"),
        },
        ventasCompras: construirVentasCompras(historial, filter, esMismoMes),
        cuentasCobrar: {
            pen: construirCuentasCobrar(estadoCC, "totalSoles"),
            usd: construirCuentasCobrar(estadoCC, "totalDolares"),
        },
        ordenesCompra: construirEstados(estadoOC),
        cotizaciones: construirEstados(estadoCT),
        inventario: construirInventario(inventarioApi),
        resumenFinanciero: {
            pen: construirResumenFinanciero("pen"),
            usd: construirResumenFinanciero("usd"),
        },
    };
}
